import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  ShoppingCart, 
  Package, 
  Users,
  Calendar,
  Bell,
  Activity,
  Clock,
  ArrowRight,
  Plus
} from 'lucide-react';
import { SwipeGestures } from './SwipeGestures';

interface MetricCard {
  title: string;
  value: string;
  change: number;
  changeText: string;
  icon: React.ComponentType<any>;
  color: string;
}

interface RecentActivity {
  id: string;
  type: 'sale' | 'product' | 'customer';
  title: string;
  description: string;
  time: string;
  amount?: number;
}

const METRICS: MetricCard[] = [
  {
    title: 'Ventas Hoy',
    value: '$2,847.50',
    change: 12.5,
    changeText: '+12.5% vs ayer',
    icon: DollarSign,
    color: 'text-success'
  },
  {
    title: 'Órdenes',
    value: '34',
    change: -5.2,
    changeText: '-5.2% vs ayer',
    icon: ShoppingCart,
    color: 'text-warning'
  },
  {
    title: 'Productos',
    value: '1,249',
    change: 2.1,
    changeText: '+2.1% vs mes pasado',
    icon: Package,
    color: 'text-primary'
  },
  {
    title: 'Clientes',
    value: '856',
    change: 8.3,
    changeText: '+8.3% vs mes pasado',
    icon: Users,
    color: 'text-success'
  }
];

const RECENT_ACTIVITIES: RecentActivity[] = [
  {
    id: '1',
    type: 'sale',
    title: 'Nueva venta #1248',
    description: 'Cliente: María González',
    time: 'Hace 5 min',
    amount: 125.50
  },
  {
    id: '2',
    type: 'product',
    title: 'Stock bajo: iPhone 15 Pro',
    description: 'Solo quedan 2 unidades',
    time: 'Hace 15 min'
  },
  {
    id: '3',
    type: 'customer',
    title: 'Nuevo cliente registrado',
    description: 'Carlos Mendoza se unió',
    time: 'Hace 1 hora'
  },
  {
    id: '4',
    type: 'sale',
    title: 'Venta #1247 completada',
    description: 'Cliente: Ana Silva',
    time: 'Hace 2 horas',
    amount: 89.99
  }
];

const QUICK_ACTIONS = [
  { title: 'Nueva Venta', icon: Plus, color: 'bg-success', path: '/sales/pos' },
  { title: 'Agregar Producto', icon: Package, color: 'bg-primary', path: '/inventory/products' },
  { title: 'Ver Reportes', icon: Activity, color: 'bg-warning', path: '/reports' },
  { title: 'Clientes', icon: Users, color: 'bg-info', path: '/customers' }
];

export function MobileDashboard() {
  const getActivityIcon = (type: RecentActivity['type']) => {
    switch (type) {
      case 'sale': return ShoppingCart;
      case 'product': return Package;
      case 'customer': return Users;
      default: return Activity;
    }
  };

  const getActivityColor = (type: RecentActivity['type']) => {
    switch (type) {
      case 'sale': return 'text-success';
      case 'product': return 'text-warning';
      case 'customer': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-md border-b border-border p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-xl font-bold">Dashboard</h1>
            <p className="text-sm text-muted-foreground">
              {new Date().toLocaleDateString('es-ES', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
          <Button variant="outline" size="sm" className="relative">
            <Bell className="h-4 w-4" />
            <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 text-xs">3</Badge>
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {/* Métricas principales */}
        <div className="p-4">
          <div className="grid grid-cols-2 gap-3 mb-6">
            {METRICS.map((metric, index) => (
              <SwipeGestures key={metric.title} className="h-full">
                <Card className="apple-card h-full cursor-pointer active:scale-95 transition-transform">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <metric.icon className={`h-4 w-4 ${metric.color}`} />
                      {metric.change > 0 ? (
                        <TrendingUp className="h-3 w-3 text-success" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-destructive" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold">{metric.value}</h3>
                      <p className="text-xs text-muted-foreground mb-1">{metric.title}</p>
                      <p className={`text-xs ${metric.change > 0 ? 'text-success' : 'text-destructive'}`}>
                        {metric.changeText}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </SwipeGestures>
            ))}
          </div>

          {/* Acciones rápidas */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-3">Acciones Rápidas</h2>
            <div className="grid grid-cols-2 gap-3">
              {QUICK_ACTIONS.map((action) => (
                <SwipeGestures key={action.title}>
                  <Card className="apple-card cursor-pointer active:scale-95 transition-transform">
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-lg ${action.color} flex items-center justify-center`}>
                          <action.icon className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-sm">{action.title}</h3>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                </SwipeGestures>
              ))}
            </div>
          </div>

          {/* Actividad reciente */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">Actividad Reciente</h2>
              <Button variant="ghost" size="sm">
                Ver todo
              </Button>
            </div>
            <div className="space-y-3">
              {RECENT_ACTIVITIES.map((activity) => {
                const ActivityIcon = getActivityIcon(activity.type);
                return (
                  <SwipeGestures key={activity.id}>
                    <Card className="apple-card cursor-pointer active:scale-[0.98] transition-transform">
                      <CardContent className="p-3">
                        <div className="flex items-start space-x-3">
                          <div className={`w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0`}>
                            <ActivityIcon className={`h-4 w-4 ${getActivityColor(activity.type)}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-medium text-sm">{activity.title}</h4>
                                <p className="text-xs text-muted-foreground">{activity.description}</p>
                              </div>
                              <div className="text-right ml-2">
                                {activity.amount && (
                                  <p className="font-bold text-sm text-success">
                                    +${activity.amount}
                                  </p>
                                )}
                                <div className="flex items-center text-xs text-muted-foreground">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {activity.time}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </SwipeGestures>
                );
              })}
            </div>
          </div>

          {/* Gráfico de ventas (placeholder) */}
          <Card className="apple-card mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Ventas de la Semana</CardTitle>
              <CardDescription>Comparación con la semana anterior</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-32 bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <Activity className="h-8 w-8 text-primary mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Gráfico de ventas</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}